// People: avatars in the person's colour, small stacks on tree rows and cards, and the collaboration
// layer's own line icons (bell, comment, activity), drawn like icons.tsx (16px, 1.5 stroke).
import type { PresencePeer } from "@filmsquare/realtime/presence";
import type { ReactNode } from "react";
import { firstName, initialsOf } from "../../collab/colors.ts";
import { usePeopleAt } from "../../collab/hooks.ts";
import { whereOfKey } from "../../collab/where.ts";

type CIconName = "bell" | "comment" | "activity" | "eye" | "resolve" | "pin";

const CP: Record<CIconName, ReactNode> = {
  bell: (
    <>
      <path d="M4 11.5V7.5a4 4 0 0 1 8 0v4l1 1.2H3z" />
      <path d="M6.6 14a1.5 1.5 0 0 0 2.8 0" />
    </>
  ),
  comment: (
    <path d="M3 3.5h10c.55 0 1 .45 1 1v6c0 .55-.45 1-1 1H8.5L5.5 14v-2.5H3c-.55 0-1-.45-1-1v-6c0-.55.45-1 1-1z" />
  ),
  activity: <path d="M2 8h2.5L6 4l3 8 1.5-4H14" />,
  eye: (
    <>
      <path d="M1.8 8S4 3.8 8 3.8 14.2 8 14.2 8 12 12.2 8 12.2 1.8 8 1.8 8z" />
      <circle cx="8" cy="8" r="1.8" />
    </>
  ),
  resolve: (
    <>
      <circle cx="8" cy="8" r="6" />
      <path d="M5.5 8.2l1.8 1.8 3.3-3.6" />
    </>
  ),
  pin: (
    <>
      <path d="M8 14s4.5-4 4.5-7.5a4.5 4.5 0 0 0-9 0C3.5 10 8 14 8 14z" />
      <circle cx="8" cy="6.5" r="1.5" />
    </>
  ),
};

export function CIcon({ name, size = 16 }: { name: CIconName; size?: number }) {
  return (
    <svg
      className="icon"
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {CP[name]}
    </svg>
  );
}

export function Avatar({
  name,
  size = 22,
  title,
  className,
}: {
  /** who it is; avatars are neutral, so only the initials tell people apart */
  userId: string | null | undefined;
  name: string;
  size?: number;
  title?: string;
  className?: string;
}) {
  return (
    <span
      className={`pc-avatar${className ? ` ${className}` : ""}`}
      style={{
        width: size,
        height: size,
        fontSize: Math.max(8, Math.round(size * 0.42)),
      }}
      title={title}
      aria-hidden={title ? undefined : true}
    >
      {initialsOf(name)}
    </span>
  );
}

/** Overlapping avatars, at most `max`, then "+n". */
export function AvatarStack({
  people,
  max = 3,
  size = 18,
  label,
}: {
  people: readonly Pick<PresencePeer, "id" | "userId" | "name">[];
  max?: number;
  size?: number;
  label?: string;
}) {
  if (people.length === 0) return null;
  const shown = people.slice(0, max);
  const more = people.length - shown.length;
  const names = people.map((p) => firstName(p.name)).join(", ");
  return (
    <span className="pc-stack" role="img" aria-label={label ?? `Here now: ${names}`} title={names}>
      {shown.map((p) => (
        <Avatar key={p.id} userId={p.userId} name={p.name} size={size} />
      ))}
      {more > 0 && (
        <span className="pc-more" style={{ height: size, minWidth: size }}>
          +{more}
        </span>
      )}
    </span>
  );
}

/** Who else has this tree folder, card or shot open (by its key: `shot:<id>`, `folder:<id>` …). */
export function PeerDots({ itemKey, size = 16 }: { itemKey: string; size?: number }) {
  const people = usePeopleAt(whereOfKey(itemKey));
  if (people.length === 0) return null;
  return <AvatarStack people={people} size={size} max={3} />;
}
